(function () {
  if (typeof window === "undefined") return;

  // ✅ Robust WebView Detection
  const isWebView = (() => {
    const ua = navigator.userAgent || "";
    const ios = /iPhone|iPod|iPad/i.test(ua);
    const safari = /Safari/i.test(ua);
    const standalone = window.navigator ? window.navigator.standalone : false;
    const androidWV = /\bwv\b|Android.*Version\/[\d.]+/i.test(ua);
    return (ios && (!safari || standalone === true)) || androidWV;
  })();

  if (!isWebView) return;

  // ✅ One-time Soft Reload to clear old cache issues
  try {
    if (!sessionStorage.getItem("wv_refresh_once")) {
      sessionStorage.setItem("wv_refresh_once", "1");
      window.location.replace(window.location.href);
      return;
    }
  } catch {}

  // ✅ Idle scheduler
  const runIdle = (fn, timeout = 400) =>
    "requestIdleCallback" in window
      ? requestIdleCallback(fn, { timeout })
      : setTimeout(fn, timeout);

  const managedStyleAttr = "data-wv-managed-style";
  const managedScriptAttr = "data-wv-managed-script";
  const cachePrefix = "meama_tab_cache_";

  // Paths that should do full reloads instead of instant swap
  const hardReloadPathnames = new Set([
    "/", // home
    "/pages/locations", // maps
    "/collections/all-products" // products
  ]);
  const isHardReloadPath = (pathname) => hardReloadPathnames.has(normalizePathname(pathname));

  const normalizePathname = (pathname) =>
    pathname.replace(/\/+$/, "") || "/";
  const normalizeUrl = (input) => {
    try {
      const url = input instanceof URL ? input : new URL(input, location.href);
      if (url.origin !== location.origin) return null;
      const pathname = normalizePathname(url.pathname);
      const search = url.search || "";
      const originPath = `${url.origin}${pathname}`;
      const absolute = `${originPath}${search}`;
      return { url, pathname, search, originPath, absolute };
    } catch {
      return null;
    }
  };

  // =========================
  // ⚙️ Cache Setup
  // =========================
  // First-paint guard: hide header/footer on the very first visible load
  let firstPaintGuardApplied = false;
  const firstPaintGuardId = "wv-first-paint-hide";
  const applyFirstPaintGuard = () => {
    if (firstPaintGuardApplied) return;
    firstPaintGuardApplied = true;
    try {
      const style = document.createElement("style");
      style.id = firstPaintGuardId;
      style.setAttribute("data-wv-managed-style", "1");
      style.textContent = `
        /* Keep announcement bar visible */
        #shopify-section-announcement-bar, .announcement-bar { display: block !important; visibility: visible !important; opacity: 1 !important; }
        /* Hide header/footer shells */
        header, header.header, header.site-header, #shopify-section-header, [data-section-type="header"],
        footer, .footer, #shopify-section-footer, [data-section-type="footer"] { display: none !important; }
      `;
      const anchor = document.head.querySelector("style, link[rel='stylesheet']");
      if (anchor) document.head.insertBefore(style, anchor); else document.head.appendChild(style);
    } catch {}
  };
  const releaseFirstPaintGuard = () => {
    try {
      const s = document.getElementById(firstPaintGuardId);
      if (s) s.remove();
    } catch {}
  };
  try {
    if (!sessionStorage.getItem("wv_first_paint_hidden")) {
      sessionStorage.setItem("wv_first_paint_hidden", "1");
      applyFirstPaintGuard();
      // Safety: drop guard shortly after full load
      window.addEventListener("load", () => runIdle(releaseFirstPaintGuard, 600), { once: true });
    }
  } catch {}

  let dockLinks = [];
  const allowedCacheTargets = new Set();

  const refreshCacheTargets = () => {
    allowedCacheTargets.clear();
    // Primary: links in the bottom mobile dock
    const dockAnchors = [...document.querySelectorAll("mobile-dock a[href]")];
    // Opt-in: any link explicitly marked cacheable anywhere in the page
    const optInAnchors = [...document.querySelectorAll("a[href][data-wv-cache]")];
    const anchors = [...new Set([...dockAnchors, ...optInAnchors])];
    dockLinks = dockAnchors;

    const home = normalizeUrl(`${location.origin}/`);
    if (home && !isHardReloadPath(home.pathname)) allowedCacheTargets.add(home.originPath);

    // Force locations to always be cacheable like products
    const locations = normalizeUrl(`${location.origin}/pages/locations`);
    if (locations && !isHardReloadPath(locations.pathname))
    allowedCacheTargets.add(locations.originPath);


    anchors.forEach((a) => {
      const href = a.getAttribute("href");
      const normalized = normalizeUrl(href);
      if (normalized && !isHardReloadPath(normalized.pathname)) allowedCacheTargets.add(normalized.originPath);
    });
  };

  const getCacheKey = (abs) => cachePrefix + abs;

  const createCacheSnapshot = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const main = doc.querySelector("main");
      if (!main) return null;

      const fragments = [];
      const title = doc.querySelector("title");
      if (title) fragments.push(title.outerHTML);

      doc.querySelectorAll("link[rel='stylesheet'],style").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      // Preserve only explicitly opted-in head scripts for rehydration
      doc.querySelectorAll("head script[data-wv-exec]").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      return `<html><head>${fragments.join("")}</head><body>${main.outerHTML}</body></html>`;
    } catch {
      return null;
    }
  };

  const purgeLegacyCache = () => {
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(cachePrefix)) continue;
        const absolute = key.slice(cachePrefix.length);
        try {
          const u = new URL(absolute);
          const originPath = `${u.origin}${normalizePathname(u.pathname)}`;
          if (!allowedCacheTargets.has(originPath)) toRemove.push(key);
        } catch {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {}
  };

  const waitForStylesheet = (link) =>
    new Promise((resolve) => {
      const done = () => resolve();
      link.addEventListener("load", done, { once: true });
      link.addEventListener("error", done, { once: true });
      setTimeout(done, 1200);
    });

  // =========================
  // 🎨 Smart Style Sync
  // =========================
  const buildStyleKey = (node) => {
    if (node.tagName === "LINK") return `link:${node.href}`;
    if (node.tagName === "STYLE") return `style:${node.textContent?.trim()}`;
    return null;
  };

  const syncPageStyles = async (doc) => {
    const existing = new Map();
    document.head
      .querySelectorAll(`[${managedStyleAttr}]`)
      .forEach((n) => existing.set(buildStyleKey(n), n));

    const pending = [];

    const addStyle = (el) => {
      const key = buildStyleKey(el);
      if (!key) return;
      if (existing.has(key)) {
        existing.delete(key);
        return;
      }
      const clone = el.cloneNode(true);
      clone.setAttribute(managedStyleAttr, "1");
      // Prioritize CSS by inserting early and increasing fetch priority
      const anchor = document.head.querySelector("link[rel='stylesheet'], style");
      if (clone.tagName === "LINK") clone.setAttribute("fetchpriority", "high");
      if (anchor) document.head.insertBefore(clone, anchor); else document.head.appendChild(clone);
      if (clone.tagName === "LINK") pending.push(waitForStylesheet(clone));
    };

    doc.querySelectorAll("link[rel='stylesheet'],style").forEach(addStyle);
    existing.forEach((n) => n.remove());

    // Sync head scripts explicitly opted-in via data-wv-exec
    try {
      const existingScripts = new Map();
      document.head
        .querySelectorAll(`script[${managedScriptAttr}]`)
        .forEach((s) => {
          const k = s.src ? `src:${s.src}` : `inline:${(s.textContent||"").trim()}`;
          existingScripts.set(k, s);
        });

      const addHeadScript = (old) => {
        // Only scripts marked as safe to execute
        if (!old.hasAttribute("data-wv-exec")) return;
        const key = old.src ? `src:${old.src}` : `inline:${(old.textContent||"").trim()}`;
        if (!key) return;
        if (existingScripts.has(key)) {
          existingScripts.delete(key);
          return;
        }
        const s = document.createElement("script");
        if (old.type) s.type = old.type;
        if (old.noModule) s.noModule = true;
        // Prefer defer to avoid blocking
        if (old.defer) s.defer = true; else s.defer = true;
        if (old.async) s.async = old.async;
        if (old.src) s.src = old.src;
        s.setAttribute(managedScriptAttr, "1");
        if (!old.src) s.textContent = old.textContent || "";
        const anchorScript = document.head.querySelector("script");
        if (anchorScript) document.head.insertBefore(s, anchorScript); else document.head.appendChild(s);
      };

      doc.querySelectorAll("head script").forEach(addHeadScript);
      // Do not remove existing managed scripts aggressively; they can stay for subsequent pages
    } catch {}

    if (pending.length) await Promise.allSettled(pending);
  };

  // =========================
  // 🧭 Navigation & Caching
  // =========================
  let mainEl = document.querySelector("main") || document.body;
  let activeNav = null;
  let backgroundController = null;
  let navFetchController = null;

  refreshCacheTargets();
  purgeLegacyCache();

  const swapContent = async (html, newUrl, push = true) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const sourceMain = doc.querySelector("main") || doc.body;
    if (!sourceMain) return;

    await syncPageStyles(doc);

    const executeInlineScripts = (root) => {
      try {
        const scripts = root.querySelectorAll("script");
        scripts.forEach((old) => {
          // Avoid reloading external scripts to prevent duplicates
          if (old.src && !old.hasAttribute("data-wv-exec")) return;
          const s = document.createElement("script");
          // Copy basic attributes
          if (old.type) s.type = old.type;
          if (old.noModule) s.noModule = true;
          if (old.defer) s.defer = true;
          if (old.async) s.async = old.async;
          // Support opt-in external execution
          if (old.src && old.hasAttribute("data-wv-exec")) s.src = old.src;
          s.textContent = old.textContent || "";
          old.replaceWith(s);
        });
      } catch {}
    };

    const nextMain = sourceMain.cloneNode(true);
    document.title = doc.title || document.title;
    mainEl.replaceWith(nextMain);
    mainEl = nextMain;

    // Execute inline (and opt-in) scripts contained in swapped content
    executeInlineScripts(mainEl);

    refreshCacheTargets();
    purgeLegacyCache();

    if (push) history.pushState({}, "", newUrl);

    activeNav = null;
    // If guard somehow remained during first nav, release it now
    releaseFirstPaintGuard();
    // Rehydration hooks for site scripts
    window.dispatchEvent(new Event("meama:pageRehydrated"));
    // Fire theme's page-ready event so components re-init on swapped content
    try {
      document.dispatchEvent(new CustomEvent("page:loaded"));
      // Some theme code binds to DOMContentLoaded only; synthesize a late dispatch
      document.dispatchEvent(new Event("DOMContentLoaded"));
      // Trigger section-level init hooks and lazy observers
      try {
        const sections = mainEl.querySelectorAll('section[data-section-id], [data-section-id].shopify-section, [data-section-type]');
        sections.forEach((el) => {
          el.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
          el.dispatchEvent(new Event('section:load', { bubbles: true }));
        });
      } catch {}
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('scroll'));
    } catch {}
  };

  const updateCache = async (input) => {
    const descriptor = normalizeUrl(input);
    if (!descriptor || isHardReloadPath(descriptor.pathname) || !allowedCacheTargets.has(descriptor.originPath)) return;

    const key = getCacheKey(descriptor.absolute);
    const controller = new AbortController();
    if (backgroundController) backgroundController.abort();
    backgroundController = controller;

    try {
      const res = await fetch(descriptor.url.href, {
        cache: "no-store",
        credentials: "same-origin",
        signal: controller.signal
      });
      if (!res.ok) return;
      if (!res.headers.get("content-type")?.includes("text/html")) return;

      const html = await res.text();
      const snapshot = createCacheSnapshot(html) || html;
      // store under final URL if redirected
      const finalDesc = normalizeUrl(res.url || descriptor.url.href) || descriptor;
      const finalKey = getCacheKey(finalDesc.absolute);
      localStorage.setItem(finalKey, snapshot);
      if (finalKey !== key) {
        try { localStorage.setItem(key, snapshot); } catch {}
      }
      return snapshot;
    } catch {
    } finally {
      if (backgroundController === controller) backgroundController = null;
    }
  };

  const sequentialPrecache = async (links) => {
    for (const a of links) {
      if (activeNav) return;
      const href = a.getAttribute("href");
      const n = normalizeUrl(href);
      if (!n || isHardReloadPath(n.pathname) || !allowedCacheTargets.has(n.originPath)) continue;
      await new Promise((r) => runIdle(async () => {
        if (activeNav) return r();
        await updateCache(n.absolute);
        r();
      }, 500));
    }
  };

  // =========================
  // 🚀 Initial Preload + Defer JS
  // =========================
  window.addEventListener("load", () => {
    runIdle(async () => {
      refreshCacheTargets();
      purgeLegacyCache();
      try {
        await updateCache(`${location.origin}/`);
        await updateCache(`${location.origin}/pages/locations`);
        await updateCache(`${location.origin}/collections/all-products`);
      } catch {}
      sequentialPrecache(dockLinks);
    }, 150);

    // 🧠 Lazy-load heavy scripts
    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 600);
  });

  // =========================
  // 🖱️ Instant Navigation
  // =========================
  document.body.addEventListener("click", (e) => {
    const a = e.target.closest("a[href]");
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
    // Respect opt-out and non-standard navigations
    if (a.hasAttribute("download")) return;
    const target = a.getAttribute("target");
    if (target && target.toLowerCase() !== "_self") return;
    if (a.hasAttribute("data-no-instant") || a.hasAttribute("data-no-wv")) return;

    let url;
    try {
      url = new URL(href, location.href);
    } catch {
      return;
    }

    if (url.origin !== location.origin) return;
    // Allow same pathname with different queries to be handled
    const samePath = normalizePathname(url.pathname) === normalizePathname(location.pathname);
    const sameQuery = (url.search || "") === (location.search || "");
    if (samePath && sameQuery) return;
    const descriptor = normalizeUrl(url);
    if (!descriptor) return;

    // Safety: do not intercept sensitive flows
    const path = descriptor.pathname;
    if (/^(?:\/checkout\b|\/account\b|\/cart\b|\/apps?\b|\/a\/)/i.test(path)) return;
    // Only intercept paths we manage (ignore query) and not hard-reload ones
    if (isHardReloadPath(descriptor.pathname) || !allowedCacheTargets.has(descriptor.originPath)) return;

    e.preventDefault();
    activeNav = descriptor.absolute;
    if (backgroundController) backgroundController.abort();
    if (navFetchController) navFetchController.abort();

    const key = getCacheKey(descriptor.absolute);
    const cached = localStorage.getItem(key);

    if (cached) {
      swapContent(cached, descriptor.url.href).then(() => runIdle(() => updateCache(descriptor.absolute), 200));
      return;
    }

    const controller = new AbortController();
    navFetchController = controller;

    fetch(descriptor.absolute, { credentials: "same-origin", signal: controller.signal })
      .then(async (r) => {
        if (!r.ok) throw "Network";
        const html = await r.text();
        return { html, final: normalizeUrl(r.url) || descriptor };
      })
      .then(({ html, final }) => {
        if (controller !== navFetchController || activeNav !== descriptor.absolute) return;
        const snapshot = createCacheSnapshot(html) || html;
        const finalKey = getCacheKey(final.absolute);
        localStorage.setItem(finalKey, snapshot);
        if (finalKey !== key) {
          try { localStorage.setItem(key, snapshot); } catch {}
        }
        return swapContent(snapshot, final.url.href);
      })
      .catch(() => (window.location.href = descriptor.url.href))
      .finally(() => {
        if (controller === navFetchController) navFetchController = null;
      });
  });

  window.addEventListener("popstate", () => {
    const d = normalizeUrl(location.href);
    if (!d) return;
    if (isHardReloadPath(d.pathname) || !allowedCacheTargets.has(d.originPath)) return;
    let cached = localStorage.getItem(getCacheKey(d.absolute));
    if (!cached) cached = localStorage.getItem(getCacheKey(d.originPath));
    if (cached) swapContent(cached, d.url.href, false);
  });

  // =========================
  // 🧩 Rehydration + Save Snapshot
  // =========================
  window.addEventListener("meama:pageRehydrated", () => {
    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 200);
    runIdle(() => sequentialPrecache(dockLinks), 600);
  });

  window.addEventListener("pagehide", () => {
    try {
      const d = normalizeUrl(location.href);
      if (!d || !allowedCacheTargets.has(d.originPath)) return;
      const snapshot = createCacheSnapshot(document.documentElement.outerHTML) ||
        document.documentElement.outerHTML;
      localStorage.setItem(getCacheKey(d.absolute), snapshot);
    } catch {}
  });
})();
